import { useTranslation } from "react-i18next"
import { ArrowSquareOut, Brain, PencilSimple } from "@phosphor-icons/react"
import { ListItem, ListText } from "@/components/ui/list"
import { Tag } from "@/components/ui/tag"
import { joinStrings } from "@/lib/joinString"
import { cn } from "@/lib/utils"
import { chNeuropsychologicDiagnosticPrescriptionDrawerFragment$key } from "@/modules/patients/detail/neuropsychologic-diagnostic-prescription/ch/__generated__/chNeuropsychologicDiagnosticPrescriptionDrawerFragment.graphql"
import { ChNeuropsychologicDiagnosticPrescriptionDrawer } from "@/modules/patients/detail/neuropsychologic-diagnostic-prescription/ch/ch-neuropsychologic-diagnostic-prescription-drawer"
import { DocumentProps } from "@/modules/patients/detail/progress-note/types/document"
import { useConcludeDialogStoreContext } from "@/modules/patients/detail/store/useConcludeDialogStore"
import { useDrawerStore } from "@/stores/useDrawerStore"
import { MedicalPrescription } from "@/types/__generated__/union-types"

interface NeuropsychologicDiagnosticsPrescriptionDocumentProps extends DocumentProps {
  id?: string
  progressNoteId: string
  neuropsychologicDiagnosticPrescriptionRef: chNeuropsychologicDiagnosticPrescriptionDrawerFragment$key
  medicalPrescriptions?: readonly MedicalPrescription[]
}

const NeuropsychologicDiagnosticsPrescriptionDocument = ({
  id,
  progressNoteId,
  neuropsychologicDiagnosticPrescriptionRef,
  medicalPrescriptions,
  isEditable = true,
  isConclusion,
  isSigned,
  isSent,
  onSelect,
}: NeuropsychologicDiagnosticsPrescriptionDocumentProps) => {
  const { t } = useTranslation()
  const selectedDocumentId = useConcludeDialogStoreContext((state) => state.selectedDocument?.id)
  const openDrawer = useDrawerStore((state) => state.openDrawer)
  const isSelected = isConclusion && selectedDocumentId === id
  const triggeredValidation = useConcludeDialogStoreContext((state) => state.triggeredValidation)

  const medicalPrescriptionInfo = medicalPrescriptions?.map((medicalPrescription) =>
    t(`patient.neuropsychologicDiagnosticPrescription.medicalPrescriptionList.${medicalPrescription}`),
  )

  return (
    <ListItem
      className={cn(
        "group hover:bg-surface-hovered flex h-12 items-center hover:cursor-pointer",
        isSelected && "bg-primary-surface hover:bg-primary-surface-hovered active:bg-primary-surface-pressed",
        !isEditable && !isConclusion && !isSent && "cursor-auto!",
      )}
      data-testid="neuropsychologic-diagnostic-prescription-document-item"
      onClick={() => {
        if (!isEditable && id) {
          onSelect(id)
          return
        }
        openDrawer(
          () => (
            <ChNeuropsychologicDiagnosticPrescriptionDrawer
              id={id}
              progressNoteId={progressNoteId}
              neuropsychologicDiagnosticPrescriptionRef={neuropsychologicDiagnosticPrescriptionRef}
            />
          ),
          "NeuropsychologicDiagnosticPrescriptionDrawer",
        )
      }}>
      <Brain className={cn("shrink-0", isSelected ? "text-on-primary-surface" : "text-on-surface-subdued")} />
      <div className={cn("ml-2 min-w-0", isEditable && "group-hover:mr-4")}>
        <ListText
          data-testid="neuropsychologic-diagnostic-prescription-header"
          className={isSelected ? "text-on-primary-surface" : ""}>
          {t("patient.neuropsychologicDiagnosticPrescription.neuropsychologicDiagnosticPrescription")}
        </ListText>
        {medicalPrescriptionInfo && (
          <ListText
            data-testid="selected-medical-prescription"
            variant="secondary"
            className={cn("truncate", isSelected && "text-on-primary-surface")}>
            {joinStrings(medicalPrescriptionInfo, ", ")}
          </ListText>
        )}
      </div>
      {isEditable && (
        <div className="ml-auto hidden group-hover:block">
          <div className="hover:bg-surface-hovered p-2 hover:rounded-full hover:p-2">
            <PencilSimple
              className="text-on-surface-subdued"
              data-testid="neuropsychologic-diagnostic-prescription-edit-button"
            />
          </div>
        </div>
      )}
      {!isConclusion && isSent && (
        <div className="ml-auto hidden group-hover:block">
          <div className="hover:bg-surface-hovered p-2 hover:rounded-full hover:p-2">
            <ArrowSquareOut
              className="text-on-surface-subdued"
              data-testid="neuropsychologic-diagnostic-prescription-view-document-button"
            />
          </div>
        </div>
      )}
      {isConclusion && (triggeredValidation || isSigned) && (
        <Tag
          className="ml-auto group-hover:hidden"
          type={isSigned ? "success" : "critical"}
          label={isSigned ? t("patient.conclude.signed") : t("patient.conclude.signThis")}
        />
      )}
    </ListItem>
  )
}

export { NeuropsychologicDiagnosticsPrescriptionDocument }
